import type {
  ExtendedInitiative,
  FocusData,
  Initiative,
  KPIOverviewData,
} from '../../api/initiative-client/initiative-client.type';
import type { Milestone } from '../../api/milestones-client/milestones-client.type';
import type { Board, ProjectsResponse } from '../../api/projects-client/projects-client.type';
import type { Goal, Outcome } from '../../api/strategy-client/strategy-client.type';
import type { TransformedTarget } from '../../api/targets-client/targets-client.type';
import type { Epic, MinifiedTask, StrategicEpic } from '../../api/tasks-client/task-client.type';
import type {
  FlowByPhaseDataOverTime,
  MeasureDataResponse,
  MeasureMetadata,
} from '../../api/work-periods-client/work-periods-client.type';
import { CostMode } from '../../containers/strategy/initiative-performance/cost-tab/cost-tab.type';
import type { Phase } from '../../containers/strategy/initiative-performance/flow-by-phase-tab/flow-by-phase-tab.type';
import type { PhaseLabel } from '../../helpers/phase-labels/phase-labels.type';
import {
  EpicSortOption,
  FocusMode,
  PerformanceTab,
  SortDirection,
  ThroughputMode,
} from '../../containers/strategy/initiative-performance/initiative-performance.type';
import type { RelatedTasksMode } from '../../containers/strategy/initiative-performance/related-tasks-tab/related-tasks-tab.type';
import { MeasureDataType } from '../../containers/strategy/initiative-performance/targets-tab/targets-tab.type';
import { InitiativeRecord, Metric } from '../../containers/strategy/strategy.type';

type Filters = {
  status: string[];
  state: string[];
  source: string[];
  is_active: string[];
  only_bookmarked: boolean;
  include_empty: boolean;
  parent_outcomes: string[];
  // Team (Project) ids selected on the Outcomes tab. Empty means "all teams".
  outcome_teams: string[];
  committed_next_weeks_enabled: boolean;
  committed_next_weeks_value: number;
  epic_due_date_state: string[];
  epic_projects: string[];
  epic_completion: string[];
  epic_target_end_date: string[];
  epic_created_date: string[];
};

type OverviewFilters = {
  mode_bookmarked: boolean;
  mode_committed: boolean;
  mode_include_empty: boolean;
  mode_is_active_only: boolean;
  mode_show_hidden: boolean;
  committed_next_weeks_value: number;
  selected_statuses: string[];
  view_mode: 'goals' | 'outcomes';
};

enum View {
  Table = 'table',
  TreeMap = 'tree-map',
}

enum TableGroupBy {
  Outcome = 'outcome',
  Team = 'team',
  Status = 'status',
  Source = 'source',
}

enum TreeMapMetric {
  CostByInitiative = 'cost_by_initiative',
  EffortByInitiative = 'effort_by_initiative',
}

enum Panel {
  Filters = 'filters',
  Side = 'side',
  OtherInitiatives = 'other-initiatives',
  OtherEpics = 'other-epics',
  OtherTeams = 'other-teams',
}

type State = {
  initiatives: Initiative[];
  kpiOverviewData: KPIOverviewData | null;
  view: View;
  page: number;
  selectedEpic: Epic | null;
  selectedInitiative: Initiative | null;
  selectedGoal: Goal | null;
  selectedOutcome: Outcome | null;
  initiativePerformance: ExtendedInitiative | null;
  performanceTab: PerformanceTab;
  performanceActivePanel: Panel | null;
  filters: Filters;
  overviewFilters: OverviewFilters;
  tableSortBy: keyof InitiativeRecord;
  tableSortDirection: SortDirection;
  collapsedGroups: Record<string, boolean>;
  activePanel: Panel | null;
  searchTerm: string;
  treeMapMetric: TreeMapMetric;
  tableGroupBy: TableGroupBy | null;
  epics: Epic[] | StrategicEpic[];
};

enum ThroughputType {
  Total = 'total',
  Completed = 'completed',
}

type ThroughputTabState = {
  mode: ThroughputMode;
  metric: Metric;
  selectedEpics: Epic[];
  selectedTeams: ProjectsResponse[];
  selectedEpic: Epic | null;
  selectedTeam: ProjectsResponse | null;
  selectedInitiatives: Initiative[];
  selectedInitiative: Initiative | null;
  selectedOutcomes: Outcome[];
  selectedOutcome: Outcome | null;
  selectedDate: Date | null;
  visibleThroughputTypes: Record<ThroughputType, boolean>;
};

type FocusTabState = {
  metric: Metric;
  mode: FocusMode;
  selectedTeams: ProjectsResponse[];
  selectedBoards: Board[];
  focusTeamsData: FocusData | null;
  focusBoardsData: FocusData | null;
};

type FilterControls = {
  searchTerm: string;
  sortBy: EpicSortOption;
  sortDirection: SortDirection;
  hideEmpty: boolean;
};

type FlowByPhaseTabState = {
  metric: Metric;
  selectedDate: string | null;
  selectedEpic: string | null;
  visiblePhases: Record<string, boolean>;
  data: FlowByPhaseDataOverTime | null;
  /**
   * Per-workflow display labels for canonical phase keys, sourced from the
   * `phases` companion array on the `flow_by_phase` measure response.
   * `null` when the API response omits it (older clients) — consumers
   * should pass it through `resolvePhaseLabels` for the i18n fallback.
   */
  phases: PhaseLabel[] | null;
  filterControls: FilterControls;
  useGroups: boolean;
  selectedSubGroups: Set<string>;
};

type TargetsTabState = {
  targets: TransformedTarget[];
  selectedTarget: TransformedTarget | null;
  availableMeasures: MeasureMetadata[];
  measureData: MeasureDataResponse | null;
  visibleMeasures: Record<MeasureDataType, boolean>;
};

type MilestonesTabState = {
  milestones: Milestone[];
  selectedMilestone: Milestone | null;
  metric: Metric;
  visibleThroughputTypes: Record<ThroughputType, boolean>;
};

type CostTabState = {
  mode: CostMode;
  selectedEpics: Epic[];
  selectedTeams: ProjectsResponse[];
  selectedInitiatives: Initiative[];
  filterControls: FilterControls;
  selectedEpic: Epic | null;
  selectedTeam: ProjectsResponse | null;
  selectedInitiative: Initiative | null;
};

type RelatedTasksTabState = {
  mode: RelatedTasksMode;
  selectedEpic: string | null;
  selectedTeam: string | null;
  selectedInitiative: string | null;
  selectedOutcome: string | null;
  selectedDate: Date | null;
  selectedTypes: string[];
  selectedPhase: Phase | null;
  searchTerm: string;
  page: number;
  selectedTask: MinifiedTask | null;
  filterControls: FilterControls;
  selectedThroughput: 'total' | 'completed';
  tasks: MinifiedTask[];
  useGroups: boolean;
};

export { Panel, TableGroupBy, ThroughputType, TreeMapMetric, View };
export type {
  CostTabState,
  FilterControls,
  Filters,
  FlowByPhaseTabState,
  FocusTabState,
  MilestonesTabState,
  OverviewFilters,
  RelatedTasksTabState,
  State,
  TargetsTabState,
  ThroughputTabState,
};
